import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { addDoc, collection, getDocs, query, where, Timestamp, updateDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format, startOfDay } from 'date-fns';
import { meetsMinimumDuration, minimumEndDate } from '@/lib/scheduling/scheduleWindow';
import { ar, enUS } from 'date-fns/locale';

// Utility function to convert Western numerals to Arabic-Indic numerals
const toArabicNumerals = (str: string) => {
  const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  return str.replace(/[0-9]/g, (digit) => arabicNumerals[parseInt(digit)]);
};
import { CalendarIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { useLanguage } from '@/contexts/LanguageContext';

const formSchema = z.object({
  startDate: z.date(),
  endDate: z.date(),
}).refine((data) => meetsMinimumDuration(data.startDate, data.endDate), {
  message: "minimumDuration", // Will be translated in the component
  path: ["endDate"]
});

interface AvailabilitySchedulerProps {
  onSubmitSuccess?: () => void;
  onCancel?: () => void;
  editingAvailability?: {
    id: string;
    startDate: Timestamp;
    endDate: Timestamp;
    selectedDays: { [key: number]: string[] };
  };
  /**
   * An admin booking a schedule for a doctor (#42): saved under that doctor,
   * already approved — the admin is the one who would approve it.
   */
  forCoach?: { id: string; name: string };
}

export function AvailabilityScheduler({ onSubmitSuccess, onCancel, editingAvailability, forCoach }: AvailabilitySchedulerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDays, setSelectedDays] = useState<{ [key: number]: string[] }>({});
  const { currentUser, userData } = useAuth();
  const { t, dir, language } = useLanguage();
  const dateLocale = language === 'ar' ? ar : enUS;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: editingAvailability ? editingAvailability.startDate.toDate() : new Date(),
      endDate: editingAvailability ? editingAvailability.endDate.toDate() : minimumEndDate(new Date()),
    },
  });

  // Pre-fill selected days when editing
  useEffect(() => {
    if (editingAvailability) {
      setSelectedDays(editingAvailability.selectedDays || {});
      form.reset({
        startDate: editingAvailability.startDate.toDate(),
        endDate: editingAvailability.endDate.toDate(),
      });
    }
  }, [editingAvailability, form]);

  const weekDays = [
    { value: 0, label: t('dashboard.coach.availability.days.sunday') },
    { value: 1, label: t('dashboard.coach.availability.days.monday') },
    { value: 2, label: t('dashboard.coach.availability.days.tuesday') },
    { value: 3, label: t('dashboard.coach.availability.days.wednesday') },
    { value: 4, label: t('dashboard.coach.availability.days.thursday') },
    { value: 5, label: t('dashboard.coach.availability.days.friday') },
    { value: 6, label: t('dashboard.coach.availability.days.saturday') },
  ];

  const sessions = [
    { value: 'session1', label: t('dashboard.coach.availability.sessions.session1') },
    { value: 'session2', label: t('dashboard.coach.availability.sessions.session2') },
  ];

  const toggleSession = (dayIndex: number, session: string) => {
    setSelectedDays(prev => {
      const currentSessions = prev[dayIndex] || [];
      const newSessions = currentSessions.includes(session)
        ? currentSessions.filter(s => s !== session)
        : [...currentSessions, session];

      const newSelectedDays = {
        ...prev,
        [dayIndex]: newSessions,
      };

      // Remove the day if no sessions are selected
      if (newSessions.length === 0) {
        delete newSelectedDays[dayIndex];
      }

      return newSelectedDays;
    });
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!currentUser || !userData) return;

    try {
      setIsLoading(true);

      // Validate that at least one day and session is selected
      if (Object.keys(selectedDays).length === 0) {
        toast.error(t('dashboard.coach.availability.scheduler.validation.selectAtLeastOne'));
        return;
      }

      // The doctor the schedule is for: the signed-in doctor, or the one an admin picked (#42)
      const coachId = forCoach?.id ?? currentUser.uid;

      // Check for existing overlapping availabilities (skip current one when editing)
      const availabilitiesRef = collection(db, 'availabilities');
      const q = query(
        availabilitiesRef,
        where('coachId', '==', coachId),
        where('status', 'in', ['pending', 'approved'])
      );
      const querySnapshot = await getDocs(q);
      
      const hasOverlap = querySnapshot.docs.some(doc => {
        // Skip the current availability when editing
        if (editingAvailability && doc.id === editingAvailability.id) {
          return false;
        }
        const availability = doc.data();
        const existingStart = availability.startDate.toDate();
        const existingEnd = availability.endDate.toDate();
        return (
          (values.startDate <= existingEnd && values.endDate >= existingStart) ||
          (existingStart <= values.endDate && existingEnd >= values.startDate)
        );
      });

      if (hasOverlap) {
        toast.error(t('dashboard.coach.availability.scheduler.validation.datesOverlap'));
        return;
      }

      if (editingAvailability) {
        // Update existing availability
        await updateDoc(doc(db, 'availabilities', editingAvailability.id), {
          startDate: Timestamp.fromDate(values.startDate),
          endDate: Timestamp.fromDate(values.endDate),
          selectedDays,
          status: 'pending', // Needs re-approval
          updatedAt: Timestamp.now(),
        });
      } else {
        // Create new availability document
        await addDoc(collection(db, 'availabilities'), {
          coachId,
          // Same fallback as mobile ScheduleScreen; firstName/lastName don't exist on users.
          coachName: forCoach?.name ||
            `${userData.firstNameEn || ''} ${userData.lastNameEn || ''}`.trim() ||
            `${userData.firstNameAr || ''} ${userData.lastNameAr || ''}`.trim(),
          startDate: Timestamp.fromDate(values.startDate),
          endDate: Timestamp.fromDate(values.endDate),
          selectedDays,
          ...(forCoach
            ? { status: 'approved', createdBy: currentUser.uid, approvedBy: currentUser.uid, approvedAt: Timestamp.now() }
            : { status: 'pending' }),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      toast.success(editingAvailability 
        ? t('dashboard.coach.availability.scheduler.successEdit')
        : t('dashboard.coach.availability.scheduler.success')
      );
      onSubmitSuccess?.();
    } catch (error) {
      toast.error((error instanceof Error && error.message) || t('dashboard.coach.availability.scheduler.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6" dir={dir}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('dashboard.coach.availability.scheduler.startDate')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          language === 'ar' 
                            ? toArabicNumerals(format(field.value, "PPP", { locale: dateLocale }))
                            : format(field.value, "PPP", { locale: dateLocale })
                        ) : (
                          <span>{t('dashboard.coach.availability.scheduler.pickDate')}</span>
                        )}
                        <CalendarIcon className={`${dir === 'rtl' ? 'mr-auto' : 'ml-auto'} h-4 w-4 opacity-50`} />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        // Update end date to maintain minimum 1-week duration
                        const currentEndDate = form.getValues('endDate');
                        if (date && !meetsMinimumDuration(date, currentEndDate)) {
                          form.setValue('endDate', minimumEndDate(date));
                        }
                      }}
                      disabled={(date) => date < startOfDay(new Date())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t('dashboard.coach.availability.scheduler.endDate')}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          language === 'ar' 
                            ? toArabicNumerals(format(field.value, "PPP", { locale: dateLocale }))
                            : format(field.value, "PPP", { locale: dateLocale })
                        ) : (
                          <span>{t('dashboard.coach.availability.scheduler.pickDate')}</span>
                        )}
                        <CalendarIcon className={`${dir === 'rtl' ? 'mr-auto' : 'ml-auto'} h-4 w-4 opacity-50`} />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => {
                        const startDate = form.getValues('startDate');
                        return date < minimumEndDate(startDate) || date < startOfDay(new Date());
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <h3 className="font-medium">{t('dashboard.coach.availability.scheduler.selectDaysAndSessions')}</h3>
          <div className="grid gap-4">
            {weekDays.map((day) => (
              <div key={day.value} className="flex items-center justify-between p-4 border rounded-lg">
                <span className="font-medium">{day.label}</span>
                <div className="flex gap-4">
                  {sessions.map((session) => (
                    <div key={session.value} className={`flex items-center ${dir === 'rtl' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                      <Checkbox
                        id={`${day.value}-${session.value}`}
                        checked={selectedDays[day.value]?.includes(session.value)}
                        onCheckedChange={() => toggleSession(day.value, session.value)}
                      />
                      <label
                        htmlFor={`${day.value}-${session.value}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {language === 'ar' ? toArabicNumerals(session.label) : session.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={`flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'} gap-4`}>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('dashboard.coach.availability.scheduler.cancel')}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                {t('dashboard.coach.availability.scheduler.submitting')}
              </>
            ) : (
              editingAvailability 
                ? t('common.saveChanges')
                : t('dashboard.coach.availability.scheduler.submit')
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}